import { IconChevronsLeft, IconChevronsRight, IconX } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { Logo } from 'components/common/Logo';
import TenantBadge, { TenantWordmark } from 'components/common/TenantBadge';
import { resolveActiveNavItem } from 'constants/navigation';
import { STORAGE_KEYS } from 'constants/storageKeys';
import { useTheme } from 'contexts/ThemeContext';
import { useAuth } from 'hooks/useAuth';
import { useTenant } from 'hooks/useTenant';
import { isMasterRole } from 'util/access';
import { visibleNav } from 'util/visibleNav';

const COLLAPSE_KEY = 'anablr:sidebarCollapsed';
// Shared with util/tokenManager.js, which clears this key on every logout
// path so a fresh login always starts back at the narrowed "selected"
// default below, regardless of what was chosen in the previous session.
const MASTER_VIEW_KEY = STORAGE_KEYS.MASTER_SIDEBAR_VIEW;

// Single vs double click on the footer arrow must be disambiguated — a
// single click's action is delayed this long to see if a second click
// follows (which cancels it and runs the double-click action instead).
// Standard browser double-click timing.
const DOUBLE_CLICK_MS = 250;

// Master panel's "show selected module only" view always keeps these
// specific nav items visible, by key, regardless of which module is
// currently selected — Dashboard/Platform/User & Access's own items (the
// master's control surface) plus Centers and Organizations (the nav item
// labelled "Organizations" is still keyed 'super-admins' — see
// constants/navigation.js) (but deliberately NOT Centers' sibling Startups,
// which hides like any other non-selected item). Keys must match
// `constants/navigation.js` NAV_SECTIONS_MASTER exactly.
const MASTER_ALWAYS_VISIBLE_ITEM_KEYS = new Set([
  'overview', 'onboarding', 'roles', 'super-admins', 'centers',
]);

// One size for every mark in the rail — the tenant logo at the top and the
// Anablr mark at the foot are the same square, so the two blocks line up on the
// same left edge and read as the same kind of thing. Collapsed uses it too, so
// nothing resizes when the rail narrows.
const MARK_PX = 36;
const MARK_BOX = 'h-9 w-9'; // = MARK_PX, in Tailwind's scale.

/** The square Anablr mark, sized to match a tenant logo. */
function BrandMark({ className = '' }) {
  return (
    <span
      className={`${MARK_BOX} shrink-0 items-center justify-center rounded-[10px] bg-brand/10 font-sans text-[18px] font-bold text-brand ${className}`}
      aria-hidden="true"
    >
      A
    </span>
  );
}

const itemClasses = ({ isActive }) =>
  [
    'flex items-center gap-3 rounded-[9px] px-3 py-2.5 text-[14.5px] font-medium transition-colors',
    isActive
      ? 'bg-surface text-ink shadow-[inset_3px_0_0_#FF7A66,0_1px_2px_rgba(20,20,30,.05)]'
      : 'text-slate hover:bg-black/[0.04] hover:text-ink',
  ].join(' ');

/**
 * Grouped left navigation rail.
 * - Desktop (lg+): static sticky column that can COLLAPSE to an icon-only rail
 *   (persisted). Collapse styling is applied only at the `lg` breakpoint, so the
 *   mobile drawer always renders full-width with labels.
 * - Mobile: off-canvas drawer toggled from the header, with a backdrop.
 */
export function Sidebar({ open = false, onClose }) {
  const { role, user, can, permissions, principal, hasResourceGrant } = useAuth();
  const { tenant } = useTenant();
  const { pathname } = useLocation();
  const { isDark } = useTheme();
  const hasCenter = Boolean(user?.center_id ?? user?.centerId ?? user?.center?.id);

  // A stakeholder can hold several hats at once (mentor + jury), and the merged
  // sidebar already shows both sets of modules. `role` is a single string —
  // whichever hat happens to back the RBAC membership — so on its own it
  // labelled a mentor+jury user simply "Mentor". List every hat instead.
  const ASSIGNMENT_LABEL = { mentor: 'Mentor', jury: 'Jury', founder: 'Founder' };
  const heldLabels = [
    ...new Set(
      (Array.isArray(user?.assignments) ? user.assignments : [])
        .map((a) => ASSIGNMENT_LABEL[String(a).toLowerCase()])
        .filter(Boolean),
    ),
  ];
  const roleLabel = heldLabels.length > 1
    ? heldLabels.join(' · ')
    // The platform master's role name is the bare string "master" — spell it
    // out as "Master Admin" rather than the literal (and confusing) "Master".
    : isMasterRole(role)
      ? 'Master Admin'
      : (role ? role.replace(/_/g, ' ') : 'super admin');

  // The brand block belongs to the TENANT — the center a center-scoped session
  // is in, or the organization for an organization-scoped one — so every panel
  // is headed by whose panel it is. The platform master has no tenant of its
  // own, and neither does a session whose tenant hasn't loaded yet; both keep
  // the Anablr wordmark up top so the block is never empty.
  const tenantName = tenant?.name || '';
  // 'rectangle' tenants supply their own wordmark, drawn at its own width with
  // no separate name/subtitle beside it — the same way the Anablr mark
  // renders at the sidebar's own foot. A 'square' tenant with a logo keeps the
  // badge + name layout.
  const isWideLogo = tenant?.logo_shape === 'rectangle' && Boolean(tenant?.logo_url);
  // A tenant with NO logo at all gets the "ADD YOUR LOGO" placeholder in the
  // same wide slot, whichever shape they picked — the mark is a 3.2:1 wordmark
  // and only reads at that width. It stands in for the name here rather than
  // sitting beside it: the block is 76px tall to match the page header, which
  // fits a mark and one subtitle line, not both plus a name. The name is still
  // in the page header and the center switcher.
  const needsLogo = Boolean(tenantName) && !tenant?.logo_url;
  // Sidebar subtitle is always just the role/capacity held (e.g. "Super
  // Admin") — the person's name is shown elsewhere (page header, center
  // switcher, user menu), not repeated here.
  const tenantSubtitle = roleLabel;

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const toggleCollapsed = () =>
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      } catch {
        /* ignore storage failures (private mode, etc.) */
      }
      return next;
    });

  // Master-panel-only: a second, independent toggle at the footer logo —
  // single click shows only the currently-selected module's section (hides
  // the rest), double click shows every section again. Persisted across a
  // reload the same way `collapsed` above is, but defaults to 'selected'
  // (narrowed) whenever there's no stored choice — which is always true
  // right after a fresh login, since tokenManager.clear() removes this key
  // on every logout path. So within one login it remembers your choice
  // across reloads, but each new login starts hidden/narrowed by default.
  // Gated on isMasterRole so no other role's sidebar is affected.
  const [masterView, setMasterView] = useState(() => {
    try {
      return localStorage.getItem(MASTER_VIEW_KEY) === 'all' ? 'all' : 'selected';
    } catch {
      return 'selected';
    }
  }); // 'all' | 'selected'
  const isMaster = isMasterRole(role);
  const setMasterViewPersisted = (next) => {
    setMasterView(next);
    try {
      localStorage.setItem(MASTER_VIEW_KEY, next);
    } catch {
      /* ignore storage failures (private mode, etc.) */
    }
  };
  const arrowClickTimer = useRef(null);
  const handleArrowClick = () => {
    // A double-click fires two click events before the dblclick event — the
    // second click here cancels the first's pending single-click action so
    // only the double-click handler below runs.
    if (arrowClickTimer.current) {
      clearTimeout(arrowClickTimer.current);
      arrowClickTimer.current = null;
      return;
    }
    arrowClickTimer.current = setTimeout(() => {
      arrowClickTimer.current = null;
      setMasterViewPersisted('selected');
    }, DOUBLE_CLICK_MS);
  };
  const handleArrowDoubleClick = () => {
    if (arrowClickTimer.current) {
      clearTimeout(arrowClickTimer.current);
      arrowClickTimer.current = null;
    }
    setMasterViewPersisted('all');
  };
  // Cancel a pending single-click action if the sidebar unmounts mid-wait
  // (e.g. a fast logout/navigation), so it never fires setState after unmount.
  useEffect(() => () => {
    if (arrowClickTimer.current) clearTimeout(arrowClickTimer.current);
  }, []);

  // Which modules this user can see — computed in ONE place (util/visibleNav)
  // and shared with DashboardLayout (page title + breadcrumb) and
  // resolveLandingPath (where a guard sends someone). Those three used to
  // answer the same question separately and could disagree: a mentor holding
  // only `mentors:read` saw Stakeholders here while the landing resolver sent
  // them to "No modules".
  const permsLoaded = Boolean(permissions?.permissions);
  const { sections } = visibleNav({
    role,
    principal,
    hasCenter,
    assignments: user?.assignments,
    roleNames: user?.roles,
    can,
    hasResourceGrant,
    permsLoaded,
  });

  // Highlight exactly ONE item — the most specific match (longest path wins) —
  // so a child route like /dashboard/todos/invitations doesn't also light up its
  // parent (/dashboard/todos). Same resolution the header title uses.
  const activeItem = resolveActiveNavItem(pathname, role, hasCenter, user?.assignments);
  // "Selected module only": keep an item if it's the always-visible set
  // (MASTER_ALWAYS_VISIBLE_ITEM_KEYS) or the currently active one — hide
  // every other item, and drop any section left with nothing in it (so a
  // fully-hidden module's header doesn't linger empty).
  const visibleSections =
    isMaster && masterView === 'selected'
      ? sections
          .map((s) => ({
            ...s,
            items: s.items.filter(
              (it) => MASTER_ALWAYS_VISIBLE_ITEM_KEYS.has(it.key) || it.path === activeItem?.path,
            ),
          }))
          .filter((s) => s.items.length > 0)
      : sections;

  // `lg:`-scoped so collapse never affects the mobile drawer.
  const hideWhenCollapsed = collapsed ? 'lg:hidden' : '';
  const showWhenCollapsed = collapsed ? 'lg:flex' : '';

  return (
    <>
      {/* Mobile backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={[
          'flex w-[250px] shrink-0 flex-col border-r border-hair bg-rail',
          // Mobile: fixed drawer that slides in/out.
          'fixed inset-y-0 left-0 z-50 transform transition-[width,transform] duration-200 lg:transform-none',
          open ? 'translate-x-0' : '-translate-x-full',
          // Desktop: static sticky column; width toggles with collapse. Sits
          // above the header (z-20) so the divider handle shows in full.
          'lg:sticky lg:top-0 lg:z-30 lg:h-screen lg:translate-x-0',
          collapsed ? 'lg:w-[76px]' : 'lg:w-[250px]',
        ].join(' ')}
      >
        {/* Tenant block — matches the header height + hairline. Collapsed: the
            mark + expand arrow stack under the logo. */}
        <div
          className={[
            'flex h-[68px] shrink-0 items-center justify-between gap-2 border-b border-hair px-5 lg:h-[76px]',
            collapsed ? 'lg:flex-col lg:justify-center lg:gap-1.5 lg:px-2' : '',
          ].join(' ')}
        >
          {/* Full identity — always on mobile; on lg only when expanded */}
          <div
            className={`flex min-w-0 items-center gap-2.5 ${hideWhenCollapsed}`}
            title={tenantName || undefined}
          >
            {tenantName && (isWideLogo || needsLogo) ? (
              <div className="flex min-w-0 flex-col justify-center gap-0.5">
                <TenantWordmark
                  name={tenantName}
                  logoUrl={tenant?.logo_url}
                  height={MARK_PX}
                  maxWidth={176}
                  placeholder
                />
                <div className="truncate text-[11px] font-medium capitalize tracking-wide text-silver">
                  {tenantSubtitle}
                </div>
              </div>
            ) : (
              <>
                {tenantName && (
                  <TenantBadge
                    name={tenantName}
                    logoUrl={tenant?.logo_url}
                    size={MARK_PX}
                    shape="rounded"
                    className="text-[13px]"
                  />
                )}
                <div className="flex min-w-0 flex-col justify-center">
                  {tenantName ? (
                    <span className="truncate font-sans text-[15.5px] font-semibold leading-tight tracking-[-0.01em] text-ink">
                      {tenantName}
                    </span>
                  ) : (
                    <Logo variant={isDark ? 'light' : 'dark'} size={20} />
                  )}
                  <div className="mt-0.5 truncate text-[11px] font-medium capitalize tracking-wide text-silver">
                    {tenantName ? tenantSubtitle : roleLabel}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Compact mark — lg only, when collapsed */}
          {tenantName ? (
            <TenantBadge
              name={tenantName}
              logoUrl={tenant?.logo_url}
              size={MARK_PX}
              shape="rounded"
              className={`hidden ${showWhenCollapsed} text-[13px]`}
            />
          ) : (
            <BrandMark className={`hidden ${showWhenCollapsed}`} />
          )}

          {/* Mobile close */}
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate hover:bg-black/[0.06] lg:hidden"
            aria-label="Close menu"
          >
            <IconX className="h-5 w-5" />
          </button>

          {/* Expand toggle » — collapsed only, stacked directly under the logo */}
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label="Expand sidebar"
            aria-expanded="false"
            className={`hidden ${showWhenCollapsed} h-7 w-7 items-center justify-center rounded-lg text-slate transition-colors hover:bg-black/[0.06] hover:text-ink`}
          >
            <IconChevronsRight className="h-4 w-4" />
          </button>
        </div>

        {/* Collapse handle « — expanded only; a round toggle on the
            sidebar↔content divider, vertically centred on the header row. */}
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-label="Collapse sidebar"
          aria-expanded="true"
          className={`absolute right-0 top-[38px] z-30 hidden ${collapsed ? '' : 'lg:flex'} h-7 w-7 -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full border border-hair bg-surface text-slate shadow-sm transition-colors hover:bg-mist hover:text-ink`}
        >
          <IconChevronsLeft className="h-4 w-4" />
        </button>

        <nav className="min-h-0 flex-1 overflow-y-auto px-3 pb-6">
          {visibleSections.map((section) => (
            <div key={section.title} className={collapsed ? 'lg:mt-2' : ''}>
              <div
                className={`px-3 pb-2 pt-[18px] text-[10.5px] font-semibold uppercase tracking-[0.16em] text-silver ${hideWhenCollapsed}`}
              >
                {section.title}
              </div>
              <div className="flex flex-col gap-0.5">
                {section.items.map(({ key, label, path, icon: Icon }) => {
                  const active = activeItem?.path === path;
                  return (
                    <NavLink
                      key={key}
                      to={path}
                      end={path === '/dashboard'}
                      onClick={onClose}
                      title={label}
                      className={`${itemClasses({ isActive: active })} ${collapsed ? 'lg:justify-center lg:px-0' : ''}`}
                    >
                      <Icon
                        className={`h-[19px] w-[19px] shrink-0 ${active ? 'text-brand' : 'text-silver'}`}
                      />
                      <span className={`min-w-0 flex-1 truncate ${hideWhenCollapsed}`}>{label}</span>
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Product mark — the tenant owns the top of the rail, so Anablr's own
            identity sits at the foot of it. Full logo image — mobile always,
            lg only when expanded; the compact "A" mark takes over when
            collapsed, same split as the tenant block above.
            Master panel only: the hide/show-all-modules toggle sits ON the
            logo's own coral accent triangle (the little mark at the top,
            between the "N" and the second "A") rather than beside the
            wordmark — ONLY that small triangle is clickable, not the whole
            logo. Position is the accent triangle's center within the shared
            1001×176.77 logo viewBox (see assets/logo-accent.svg), as a % so
            it stays aligned regardless of the logo's rendered size.
            Independent of the collapse-to-icons toggle above. */}
        <div
          className={[
            'mt-auto flex shrink-0 items-center gap-2.5 border-t border-hair px-5 py-3',
            collapsed ? 'lg:justify-center lg:px-2' : '',
          ].join(' ')}
        >
          <span className={`relative flex ${hideWhenCollapsed}`}>
            <Logo variant={isDark ? 'light' : 'dark'} size={16} />
            {isMaster && (
              // The logo artwork ALREADY draws this accent triangle — this
              // button is only an invisible, precisely-placed hit target on
              // top of it, not a second copy. Rendering our own visible
              // triangle here would double up with the one already in the
              // image (two arrows instead of one).
              <button
                type="button"
                onClick={handleArrowClick}
                onDoubleClick={handleArrowDoubleClick}
                aria-label={
                  masterView === 'selected'
                    ? 'Showing only the selected module — double-click to show all'
                    : 'Click to show only the selected module, double-click to show all'
                }
                aria-expanded={masterView === 'all'}
                className="absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2"
                style={{ left: '31%', top: '14%' }}
              />
            )}
          </span>
          <BrandMark className={`hidden ${showWhenCollapsed}`} />
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
